// Componente Table reutilizable

import React from 'react'
import { clsx } from 'clsx'
import { ChevronUp, ChevronDown } from 'lucide-react'

const Table = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'No hay datos disponibles',
  onSort,
  sortBy,
  sortDirection = 'asc',
  className,
  headerClassName,
  bodyClassName,
  rowClassName,
  cellClassName,
  striped = false,
  hover = true,
  bordered = false,
  compact = false,
  currentPage,
  totalPages,
  onPageChange,
  perPage,
  onPerPageChange,
  ...restProps
}) => {
  const tableClasses = clsx(
    'min-w-full divide-y divide-gray-200 dark:divide-gray-700',
    bordered && 'border border-gray-200 dark:border-gray-700',
    className
  )
  
  const headerClasses = clsx(
    'bg-gray-50 dark:bg-gray-800',
    headerClassName
  )
  
  const bodyClasses = clsx(
    'bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700',
    bodyClassName
  )
  
  const getRowClasses = (index) => clsx(
    striped && index % 2 === 1 && 'bg-gray-50 dark:bg-gray-800/50',
    hover && 'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-150',
    rowClassName
  )
  
  const getCellClasses = () => clsx(
    compact ? 'px-3 py-2' : 'px-6 py-4',
    'text-sm text-gray-900 dark:text-gray-100',
    cellClassName
  )
  
  const handleSort = (column) => {
    if (!onSort || !column.sortable) return
    
    const newDirection = sortBy === column.key && sortDirection === 'asc' ? 'desc' : 'asc'
    onSort(column.key, newDirection)
  }
  
  const getSortIcon = (column) => {
    if (!column.sortable) return null
    
    if (sortBy === column.key) {
      return sortDirection === 'asc' ? (
        <ChevronUp className="h-4 w-4" />
      ) : (
        <ChevronDown className="h-4 w-4" />
      )
    }
    
    return <ChevronUp className="h-4 w-4 opacity-30" />
  }
  
  const safeData = Array.isArray(data) ? data : []
  if (!Array.isArray(data)) {
    console.warn('Table: expected array for data prop, got', data)
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-primary-200 dark:border-primary-800 rounded-full animate-spin"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
        </div>
      </div>
    )
  }
  
  if (safeData.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        </div>
        <p className="text-gray-500 dark:text-gray-400 font-medium">{emptyMessage}</p>
      </div>
    )
  }
  
  // Filtrar props que no deben ir al elemento table
  const tableProps = Object.keys(restProps).reduce((acc, key) => {
    // Solo pasar props válidos para elementos HTML
    if (!['currentPage', 'totalPages', 'onPageChange', 'perPage', 'onPerPageChange'].includes(key)) {
      acc[key] = restProps[key]
    }
    return acc
  }, {})

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className={tableClasses} {...tableProps}>
          <thead className={headerClasses}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={clsx(
                    compact ? 'px-3 py-2' : 'px-6 py-3',
                    'text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider',
                    column.sortable && 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150',
                    column.className
                  )}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label || column.title}</span>
                    {column.sortable && getSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={bodyClasses}>
            {safeData.map((row, index) => (
              <tr key={row.id || index} className={getRowClasses(index)}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={clsx(
                      getCellClasses(),
                      column.cellClassName
                    )}
                  >
                    {column.render ? column.render(row, index) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {currentPage !== undefined && totalPages !== undefined && (
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Mostrar</span>
            <select
              value={perPage || 10}
              onChange={(e) => {
                const newPerPage = parseInt(e.target.value)
                onPerPageChange?.(newPerPage)
              }}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">registros por página</span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
            >
              Anterior
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return pageNum
              }).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => onPageChange?.(pageNum)}
                  className={clsx(
                    'px-3 py-1 border rounded-lg text-sm font-medium transition-colors duration-150',
                    currentPage === pageNum
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
            >
              Siguiente
            </button>
          </div>

          <div className="text-sm text-gray-600 dark:text-gray-400">
            Página <span className="font-medium">{currentPage}</span> de <span className="font-medium">{totalPages}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default Table
